import {
  booleanState,
  changingNumber,
  count,
  dollars,
  eventWindow,
  health,
  noValues,
  pastWindows,
  selection,
  signedMoney,
  stateChanges,
  subscription,
  yesNo,
  type SourceCategory,
} from "./crmFilterTaxonomy.vocabulary";
/** Exact v1.5 source rows, categories 1–14; never infer data availability here. */
export const CRM_TAXONOMY_AUTOMATION_CATEGORIES = [
  {
    id: "status_health",
    number: 1,
    label: "Status & health",
    fields: [
      [
        "user_status",
        "User status",
        stateChanges,
        [
          "Organisation user",
          "Partner user",
          "Partner client user",
          "Disabled",
        ],
      ],
      ["user_health", "User health", stateChanges, health],
      [
        "organisation_status",
        "Organisation status",
        stateChanges,
        subscription,
      ],
      ["organisation_health", "Organisation health", stateChanges, health],
      ["partner_status", "Partner status", stateChanges, subscription],
      ["partner_health", "Partner health", stateChanges, health],
      [
        "partner_client_status",
        "Partner client status",
        stateChanges,
        ["Invited", "Linked", "Deactivated"],
      ],
      ["access_type", "Access type", selection, ["Primary", "Admin", "View"]],
      [
        "contact_status",
        "Contact status",
        stateChanges,
        [
          "Lead (business owner)",
          "Lead (partner)",
          "Organisation contact",
          "Partner contact",
          "Disqualified",
        ],
      ],
      [
        "contact_health",
        "Contact health",
        stateChanges,
        ["Cold", "Warm", "Hot"],
      ],
    ],
  },
  {
    id: "setup",
    number: 2,
    label: "Setup",
    fields: [
      [
        "accounting_connection",
        "Accounting connection",
        [
          "Is",
          "Is not",
          "Has connected",
          "Has disconnected",
          "Has failed",
          "Has reconnected",
        ],
        ["Xero", "QuickBooks"],
      ],
      [
        "data_sync",
        "Data sync",
        [
          "Has completed",
          "Has failed",
          "Has been delayed",
          "Has not completed within X hours",
        ],
        ["Initial sync", "Daily sync", "Manual sync"],
      ],
      [
        "business_profile",
        "Business profile",
        ["Is complete", "Is not complete", "Has been updated"],
        ["Currency", "Country", "Financial year"],
      ],
      [
        "cash_accrual_setup",
        "Cash/accrual setup",
        ["Is", "Is not", "Has changed to"],
        ["Cash", "Accrual"],
      ],
      [
        "area_of_interest",
        "Area of interest",
        ["Is", "Is not", "Is any of", "Has changed to"],
        ["Profitability", "Cash position", "Solvency", "Cash runway"],
      ],
      [
        "data_refreshed",
        "Data refreshed",
        ["Is within", "More than", "Never"],
        pastWindows,
      ],
    ],
  },
  {
    id: "product_intelligence",
    number: 3,
    label: "Product intelligence",
    fields: [
      [
        "feature_carousel",
        "Feature carousel",
        [
          "Is not started",
          "Is in progress",
          "Is skipped",
          "Is complete",
          "Is not complete after X days",
        ],
        [
          "Budget Editor",
          "Scenario Calculator",
          "Revenue",
          "Expenses",
          "Profit",
          "Balance Sheet",
          "Cashflow",
          "Forecast",
          "Owner Funds",
          "Targets",
          "Deep dive",
          "Custom Reports",
          "Help/support",
          "User Settings",
          "Health Check",
        ],
      ],
      ["carousels_complete", "Carousels complete", count, noValues],
      [
        "feature_used",
        "Feature used",
        [
          "Has used",
          "Has not used",
          "Has used within X days",
          "Has not used in X days",
          "Has used more than X times",
        ],
        [
          "Budget Editor",
          "Scenario Calculator",
          "Revenue",
          "Expenses",
          "Profit",
          "Balance Sheet",
          "Cashflow",
          "Owner Funds",
          "Targets",
          "Deep dive",
          "Custom Reports",
          "Health Check",
          "Partner Dashboard",
        ],
      ],
      [
        "feature_activation",
        "Feature activation",
        ["Has completed", "Has not completed", "Has completed after X days"],
        [
          "First budget edit",
          "First AI recommendation approved",
          "First scenario created",
          "First deep dive created",
          "First health check generated",
          "First owner funds setup completed",
        ],
      ],
      [
        "emotional_score",
        "Emotional score",
        [
          "Is",
          "Is not",
          "Is any of",
          "Is at or below",
          "Is at or above",
          "Has changed to",
        ],
        [
          "I'm overwhelmed",
          "I'm stressed",
          "I'm getting by",
          "I'm feeling good",
          "I'm confident",
        ],
      ],
    ],
  },
  {
    id: "budget_editor",
    number: 4,
    label: "Budget editor",
    fields: [
      [
        "budget_line",
        "Budget line",
        [
          "Has added",
          "Has edited",
          "Has deleted",
          "Is missing",
          "Has changed by more than X%",
        ],
        [
          "Revenue line",
          "Expense line",
          "Balance sheet line",
          "Customer line",
          "Custom line",
        ],
      ],
      [
        "budget_editor_opened",
        "Budget editor opened",
        eventWindow,
        pastWindows,
      ],
    ],
  },
  {
    id: "cashflow_runway",
    number: 5,
    label: "Cashflow & runway",
    fields: [
      [
        "runway",
        "Runway",
        [
          "Is below",
          "Is above",
          "Has dropped below",
          "Has risen above",
          "Has changed by X",
        ],
        ["1 month", "2 months", "3 months", "6 months", "12 months"],
      ],
      ["net_cash", "Net cash", signedMoney, dollars],
      ["bank_balance", "Bank balance", changingNumber, dollars],
    ],
  },
  { id: "revenue", number: 6, label: "Revenue", fields: [] },
  { id: "expenses", number: 7, label: "Expenses", fields: [] },
  {
    id: "profit",
    number: 8,
    label: "Profit",
    fields: [
      ["net_profit", "Net profit", signedMoney, dollars],
      ["profit_margin", "Profit margin", changingNumber, ["Custom percentage"]],
    ],
  },
  { id: "balance_sheet", number: 9, label: "Balance sheet", fields: [] },
  {
    id: "financial_health_check",
    number: 10,
    label: "Financial Health Check",
    fields: [
      [
        "health_check",
        "Health check",
        [
          "Has generated",
          "Has not generated",
          "Has shared",
          "Has downloaded",
          "Has not generated in X months",
        ],
        noValues,
      ],
    ],
  },
  {
    id: "targets",
    number: 11,
    label: "Targets",
    fields: [
      [
        "target",
        "Target",
        ["Has created", "Has updated", "Has hit", "Has missed", "Is at risk"],
        ["Revenue", "Expenses", "Profit", "Cashflow", "Owner Pay", "Custom"],
      ],
    ],
  },
  {
    id: "partner_client",
    number: 12,
    label: "Partner & client",
    fields: [
      ["partner_client_added", "Partner client added", eventWindow, noValues],
      ["partner_sourced", "Partner sourced", booleanState, yesNo],
      ["billed_through_partner", "Billed through partner", booleanState, yesNo],
      [
        "partner_tier",
        "Partner tier",
        selection,
        ["Values to be set with the partner programme"],
      ],
    ],
  },
  {
    id: "engagement",
    number: 13,
    label: "Engagement",
    fields: [
      [
        "login_activity",
        "Login activity",
        [
          "Has logged in",
          "Has not logged in for X days",
          "Has logged in for the first time",
          "Has returned after X days",
        ],
        noValues,
      ],
    ],
  },
  {
    id: "forms",
    number: 14,
    label: "Forms",
    fields: [
      [
        "form_submitted",
        "Form submitted",
        ["Has submitted", "Has not submitted", "Has submitted within X days"],
        ["Live form list from Forms"],
      ],
      ["created_from_form", "Created from form", booleanState, yesNo],
    ],
  },
] as const satisfies readonly SourceCategory[];
